import * as React from "react"

import { Check, ChevronsUpDown, Search, X } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export interface MultiSelectProps {
  options: { value: string; label: string; disabled?: boolean }[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  emptyMessage?: string
  maxBadges?: number
}

export function MultiSelect({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select options",
  className,
  disabled = false,
  emptyMessage = "No options found.",
  maxBadges,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [contentWidth, setContentWidth] = React.useState<number | undefined>(undefined)

  // Sync content width with trigger width when opened
  React.useLayoutEffect(() => {
    if (open && triggerRef.current) {
      setContentWidth(triggerRef.current.offsetWidth)
    }
  }, [open])

  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [options, searchQuery])

  const handleSelect = (value: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value))
    } else {
      onChange([...selected, value])
    }
    // Keep popover open; do not change `open` here
  }

  const handleRemove = (e: React.MouseEvent, value: string) => {
    e.preventDefault()
    e.stopPropagation()
    onChange(selected.filter((item) => item !== value))
  }

  const visibleSelected = maxBadges !== undefined ? selected.slice(0, maxBadges) : selected
  const hiddenCount = selected.length - visibleSelected.length

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onChange([])
  }

  return (
    // The trigger is a div, which Radix opens on click whether or not it's disabled
    <Popover open={open && !disabled} onOpenChange={(next) => setOpen(next && !disabled)}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          className={cn(
            "flex min-h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
        >
          <div className="flex min-w-0 flex-wrap gap-1">
            {selected && selected.length > 0 ? (
              visibleSelected.map((value) => (
                <Badge key={value} variant="secondary" className="mb-1 max-w-full">
                  <span className="truncate">
                    {options.find((option) => option.value === value)?.label || value}
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={(e) => handleRemove(e, value)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            {hiddenCount > 0 && (
              <Badge variant="secondary" className="mb-1">
                +{hiddenCount} more
              </Badge>
            )}
          </div>
          <div className="flex shrink-0 items-center justify-center">
            {selected && selected.length > 0 && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-1 mr-1"
                onClick={handleClear}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear</span>
              </Button>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="p-0 w-full"
        align="start"
        style={{ width: contentWidth }}
        onWheel={(event) => {
          event.stopPropagation()
        }}
      >
        {/* Search Input */}
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        {/* Options List */}
        <div className="max-h-64 overflow-y-auto p-1">
          {filteredOptions.length === 0 ? (
            <div className="py-6 text-center text-sm">{emptyMessage}</div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selected && selected.includes(option.value)
              // A disabled option can't be added, but an already-selected one can still be removed
              const isDisabled = option.disabled && !isSelected
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={isDisabled}
                  onClick={(e) => handleSelect(option.value, e)}
                  className={cn(
                    "w-full text-left relative flex cursor-default select-none items-center justify-between rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground mb-1",
                    isSelected && "bg-accent text-accent-foreground",
                    isDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
                  )}
                >
                  <span>{option.label}</span>
                  {isSelected && <Check className="h-4 w-4" />}
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
